import { useI18n } from 'i18n-utils'
import { ContractServices, OrderDirection, TriggerMode, TriggerType } from 'network'
import { UserDoubleContractUnitType } from '../constants/kline'
import { StorageIndexDBEnum } from '../storage/StorageDB'
import { useBaseInfoStore } from './use-base-info'
import { useExchangeInfoStore } from './use-exchange-info'
import { useProjectTheme } from './use-project-theme'

export const useUserCenterContractOrderStore = defineStore('userCenterContractOrder', () => {
  const contractOrderList = ref<ApiResponse.ClosePosition[]>([])
  const defaultPageSize = 50
  const exchangeStore = useExchangeInfoStore()
  const baseInfoStore = useBaseInfoStore()
  const { t } = useI18n()
  const themeStore = useProjectTheme()
  const historicalTradeList = ref<ApiResponse.HistoricalTrade[]>([])
  const loadingHistoricalTrade = ref(true)
  const loadingHistoricalCommission = ref(true)
  const loadingHistoricalStrategy = ref(true)
  const loadingFundFlow = ref(true)
  const userDoubleContractUnitType = ref(window.localStorage.getItem(StorageIndexDBEnum.UserDoubleContractUnitType) || UserDoubleContractUnitType.USDT)

  const closePositionId = ref('')
  const trustId = ref('')

  const historicalStrategyList = ref<ApiResponse.HistoricalStrategy[]>([])

  const isMoreFundFlow = ref(true)

  const getHistoricalStrategyList = async () => {
    const res = await ContractServices.getHistoricalStrategy({ page: 1, pageSize: defaultPageSize })
    historicalStrategyList.value = res.data.list
    loadingHistoricalStrategy.value = false
  }

  const getContractOrderList = async (data?: { positionId?: string }) => {
    closePositionId.value = data?.positionId || ''
    const res = await ContractServices.getClosePositions({ positionId: closePositionId.value, pageSize: defaultPageSize })
    contractOrderList.value = res.data
    loadingHistoricalTrade.value = false
  }

  const getHistoricalTradeList = async (data?: { positionId?: string; trustId?: string }) => {
    closePositionId.value = data?.positionId || ''
    trustId.value = data?.trustId || ''
    const res = await ContractServices.getHistoricalTrade({ pageSize: defaultPageSize, opType: '1', trustId: trustId.value })
    historicalTradeList.value = res.data
    loadingHistoricalTrade.value = false
  }

  const refreshContractOrderList = async () => {
    closePositionId.value = ''
    await getContractOrderList()
  }

  const getCloseType = (data: ApiResponse.ClosePosition) => {
    const map = {
      1: '主动平仓',
      2: '强制平仓',
    }
    return map[data.closeType as keyof typeof map] || '自动减仓'
  }

  const getMaxPosition = (data: ApiResponse.ClosePosition) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(data.symbolId)
    return data.maxHoldQty.multiplyStr(config?.contractValue).formatCommonMoney()
  }

  const getOpenName = (symbolId: SymbolId) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(symbolId)
    const item = baseInfoStore.getCoinInfoByItemId(config!.baseItemId)
    return item?.itemName.toUpperCase()
  }

  const getCommissionQty = (data: ApiResponse.HistoricalCommission) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(data.symbolId)
    if (data.qty === -1 || (data.type === 2 && [5, 6].includes(data.status))) return t('全部')
    if (userDoubleContractUnitType.value === UserDoubleContractUnitType.Coin) {
      return data.qty.multiplyStr(config?.contractValue).formatCommonMoney()
    }
    const qty = data.qty.multiplyStr(config?.contractValue).removeTrailingZero.multiplyStr(data.type !== 1 ? data.avgPrice : data.price)
    if (qty.isLessThanOrEqualTo(0)) return '--'
    return qty
  }

  const getDealInterest = (data: ApiResponse.HistoricalCommission) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(data.symbolId)
    return data.qty.subtractStr(data.restQty).multiplyStr(config?.contractValue).removeTrailingZero
  }

  const getDealInterestTrade = (data: ApiResponse.HistoricalTrade) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(data.symbolId)
    return data.qty.multiplyStr(config?.contractValue).removeTrailingZero
  }

  const getDealInterestQty = (data: ApiResponse.HistoricalCommission) => {
    if (data.qty === -1 || (data.type === 2 && [5, 6].includes(data.status))) return t('全部')
    if (userDoubleContractUnitType.value === UserDoubleContractUnitType.Coin) {
      return getDealInterest(data).formatCommonMoney()
    }
    const qty = getDealInterest(data).multiplyStr(data.avgPrice)
    if (qty.isLessThanOrEqualTo(0)) return '--'
    return qty
  }

  const getOrderType = (data: ApiResponse.HistoricalCommission | ApiResponse.HistoricalTrade | ApiResponse.HistoricalStrategy) => {
    const { positionType, direction } = data
    if (positionType === OrderDirection.Buy) {
      return direction === OrderDirection.Buy ? t('开多') : t('平多')
    }
    return direction === OrderDirection.Sell ? t('开空') : t('平空')
  }

  const getHistoricalCommissionStatus = (data: ApiResponse.HistoricalCommission) => {
    let status = ''
    let statusColor = ''
    switch (data.status) {
      case 1:
        status = t('待成交')
        statusColor = themeStore.theme.colors.text.secondary
        break
      case 2:
        status = t('部分成交')
        statusColor = themeStore.theme.colors.text.secondary
        break
      case 3:
        status = t('完全成交')
        statusColor = themeStore.theme.colors.success
        break
      case 4:
        if (getDealInterest(data).isGreaterThan(0)) {
          status = t('部分成交-已撤销')
        } else {
          status = t('已撤销')
        }
        statusColor = themeStore.theme.colors.text.tertiary
        break
      case 5:
        status = t('已过期')
        statusColor = themeStore.theme.colors.warning
        break
      case 6:
        status = t('已失效')
        statusColor = themeStore.theme.colors.error
        break
      default:
        return { status: '', statusColor: '' }
    }
    if (data.liquidated === 1) {
      status = t('强制平仓')
      statusColor = themeStore.theme.colors.error
    }
    if (data.deleveraged === 1) {
      status = t('自动减仓')
      statusColor = themeStore.theme.colors.warning
    }
    return { status, statusColor }
  }

  /** 冰山委托委托总量 */
  const getIcebergTotalQty = (data: ApiResponse.HistoricalStrategy) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(data.symbolId)
    return data.totalQty.multiplyStr(config?.contractValue).removeTrailingZero
  }

  /** 冰山委托已成交量 */
  const getIcebergDealInterestQty = (data: ApiResponse.HistoricalStrategy) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(data.symbolId)
    return data.alreadyQty.multiplyStr(config?.contractValue).removeTrailingZero
  }

  /** 冰山委托单笔委托量 */
  const getIcebergSingleQty = (data: ApiResponse.HistoricalStrategy) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(data.symbolId)
    return data.singleQty.multiplyStr(config?.contractValue).removeTrailingZero
  }

  /** 获取冰山委托触发条件 */
  const getIcebergStrategyTriggerCondition = (data: ApiResponse.HistoricalStrategy) => {
    if (data.triggerMode !== TriggerMode.Immediate) {
      const temp = data.triggerType === TriggerType.Mark ? t('标记价格') : t('最新价格')
      return `${temp} | ${data.triggerPrice.formatCommonMoney()}`
    }
    return t('立即触发')
  }

  const refreshHistoricalStrategyList = async () => {
    await getHistoricalStrategyList()
  }

  const getSettleItemName = (symbolId: SymbolId) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(symbolId)

    return baseInfoStore.getCoinInfoByItemId(config!.marginItemId)?.itemName.toUpperCase()
  }

  const getSettleItemIcon = (symbolId: SymbolId) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(symbolId)
    return baseInfoStore.getCoinInfoByItemId(config!.marginItemId)?.itemDetail.coinUrl
  }

  const getCloseInterest = (data: ApiResponse.ClosePosition) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(data.symbolId)
    return data.rlzCloseQty.multiplyStr(config?.contractValue).removeTrailingZero
  }

  const getFundFlowType = (data: ApiResponse.FundFlow) => {
    const map = {
      10016: t('跟单分润'),
      10003: t('转入'),
      10004: t('转出'),
      2: t('已实现盈亏'),
      3: t('资金费用'),
      1: t('手续费'),
      4: t('强平清算费'),
    }
    return map[data.originType as keyof typeof map] || t('未知')
  }

  const getHistoricalStrategyStatus = (data: ApiResponse.HistoricalStrategy) => {
    let status = ''
    let statusColor = ''
    switch (data.status) {
      case 1:
        status = t('等待信号')
        statusColor = themeStore.theme.colors.warning
        break

      case 2:
        status = t('运行中')
        statusColor = themeStore.theme.colors.success

        break
      case 3:
        status = t('已停止')
        statusColor = themeStore.theme.colors.text.tertiary
        break
      case 4:
        status = t('已失败')
        statusColor = themeStore.theme.colors.error
        break
      default:
        break
    }
    return { status, statusColor }
  }

  const getEntrustType = (entrust: ApiResponse.HistoricalCommission) => {
    let entrustType = ''
    switch (entrust.type) {
      case 1:
        entrustType = '限价'
        break
      case 2:
        entrustType = '市价'
        break
      case 3:
        entrustType = '止损'
        break
      case 4:
        entrustType = '止盈'
        break
      case 5:
        entrustType = '市价止损'
        break
      case 6:
        entrustType = '市价止盈'
        break
      default:
        entrustType = ''
    }
    if (entrust.origType === 5) {
      entrustType = '市价止损'
    }
    if (entrust.origType === 6) {
      entrustType = '市价止盈'
    }
    return entrustType
  }

  /** 获取策略状态 */
  const getStrategyStatus = (entity: ApiResponse.DoubleContractIcebergStrategyOrder) => {
    let orderStatus = ''
    let orderStatusColor = ''
    switch (entity!.status) {
      case 1:
        orderStatus = t('等待信号')
        orderStatusColor = themeStore.theme.colors.warning
        break
      case 2:
        orderStatus = t('运行中')
        orderStatusColor = themeStore.theme.colors.success
        break
      case 3:
        orderStatus = t('已停止')
        orderStatusColor = themeStore.theme.colors.text.secondary
        break
      case 4:
        orderStatus = t('已失败')
        orderStatusColor = themeStore.theme.colors.error
        break
      default:
        return { orderStatus: '', orderStatusColor: '' }
    }
    return { orderStatus, orderStatusColor }
  }

  return {
    contractOrderList,
    getContractOrderList,
    refreshContractOrderList,
    getCloseType,
    getMaxPosition,
    getOpenName,
    getCommissionQty,
    getDealInterestQty,
    getOrderType,
    getHistoricalCommissionStatus,
    getHistoricalTradeList,
    historicalTradeList,
    getDealInterest,
    getDealInterestTrade,
    getHistoricalStrategyList,
    historicalStrategyList,
    getIcebergTotalQty,
    getIcebergDealInterestQty,
    getIcebergSingleQty,
    getIcebergStrategyTriggerCondition,
    refreshHistoricalStrategyList,
    getSettleItemName,
    getSettleItemIcon,
    getFundFlowType,
    getCloseInterest,
    getHistoricalStrategyStatus,
    loadingHistoricalTrade,
    loadingHistoricalCommission,
    loadingHistoricalStrategy,
    loadingFundFlow,
    isMoreFundFlow,
    getEntrustType,
    getStrategyStatus,
    userDoubleContractUnitType,
  }
})
